/**
 * CaptchaField — the VISIBLE, interactive "human verification" widget rendered on the auth surfaces
 * (login, lab self-signup). It is the UI face of the stateless captcha proof the server gate consumes
 * (server/src/platform/captcha): a real, clickable affordance instead of the previous invisible
 * background solve.
 *
 * Provider is auto-detected (mirrors `activeProvider()` server-side):
 *   • PoW (default, no `VITE_TURNSTILE_SITE_KEY`) — a Cloudflare-Turnstile-style bordered box with a
 *     clickable checkbox + "Je ne suis pas un robot". Clicking (or Enter/Space) runs `solveCaptcha()`
 *     which fetches + solves the signed PoW challenge, then reports the proof via `onVerified`.
 *   • Turnstile (when `VITE_TURNSTILE_SITE_KEY` is set) — the existing dependency-free TurnstileWidget
 *     renders inside the box; its minted token is exchanged via `solveCaptcha(token)` into the proof.
 *
 * Backward-compatible by design: the challenge route is available in ALL envs but the server GATE is a
 * no-op outside production, so `solveCaptcha()` returns an EMPTY proof when no challenge is issued — the
 * widget still reaches "verified" and the form stays submittable (dev + prod-build parity).
 *
 * a11y: the box is `role="checkbox"` + `aria-checked`, keyboard-activatable (Enter/Space), carries a
 * visible focus ring, and the status line is `aria-live="polite"`. The AA-contrast guarantees of the
 * auth chrome (commit c7aa309) are preserved — all colour comes from --nv-* tokens, never hardcoded.
 */
import { useCallback, useId, useRef, useState, type KeyboardEvent } from "react";
import { solveCaptcha, TURNSTILE_SITE_KEY, type CaptchaProof } from "@/api";
import { useT } from "@/app/i18n";
import { TurnstileWidget } from "./TurnstileWidget";
import { CheckIcon } from "./icons";

export type CaptchaState = "idle" | "solving" | "verified" | "error";

export interface CaptchaFieldProps {
  /** Called once the proof is built (PoW solved or Turnstile token exchanged). */
  onVerified: (proof: CaptchaProof) => void;
  /** Called when the widget falls back to an un-verified state (so the page can clear its proof). */
  onReset?: () => void;
}

/** A small inline spinner glyph (token-coloured, currentColor) — independent of the UI-kit Spinner. */
function CaptchaSpinner() {
  return <span className="nv-captcha__spin" aria-hidden />;
}

export function CaptchaField({ onVerified, onReset }: CaptchaFieldProps) {
  const { t } = useT();
  const [state, setState] = useState<CaptchaState>("idle");
  const statusId = useId();
  // Guard against a double-solve (a fast second click while the first is in flight).
  const inFlight = useRef(false);

  const isTurnstile = Boolean(TURNSTILE_SITE_KEY);
  const checked = state === "verified";
  const busy = state === "solving";

  const runSolve = useCallback(
    async (turnstileToken?: string) => {
      if (inFlight.current || state === "verified") return;
      inFlight.current = true;
      setState("solving");
      try {
        const proof = await solveCaptcha(turnstileToken);
        setState("verified");
        onVerified(proof);
      } catch {
        setState("error");
        onReset?.();
      } finally {
        inFlight.current = false;
      }
    },
    [onReset, onVerified, state],
  );

  // PoW: the box itself is the affordance. Turnstile: the embedded widget drives the solve via onToken.
  const activate = useCallback(() => {
    if (isTurnstile || busy || checked) return;
    void runSolve();
  }, [busy, checked, isTurnstile, runSolve]);

  const onKeyDown = useCallback(
    (e: KeyboardEvent<HTMLDivElement>) => {
      if (e.key === "Enter" || e.key === " " || e.key === "Spacebar") {
        e.preventDefault();
        activate();
      }
    },
    [activate],
  );

  const onToken = useCallback(
    (token: string) => {
      void runSolve(token);
    },
    [runSolve],
  );

  const retry = useCallback(() => {
    setState("idle");
    onReset?.();
  }, [onReset]);

  const statusText =
    state === "verified"
      ? t("auth.login.captcha.verified")
      : state === "solving"
        ? t("auth.login.captcha.verifying")
        : state === "error"
          ? t("auth.login.captcha.error")
          : t("auth.login.captcha.label");

  return (
    <div className={`nv-captcha is-${state}`} data-provider={isTurnstile ? "turnstile" : "pow"}>
      <div className="nv-captcha__box">
        {/* The PoW affordance: a real role=checkbox the user clicks. For Turnstile the box hosts the
            cloud widget instead and the check reflects the exchanged token. */}
        <div
          className="nv-captcha__control"
          role="checkbox"
          aria-checked={checked}
          aria-busy={busy || undefined}
          aria-describedby={statusId}
          aria-label={t("auth.login.captcha.label")}
          tabIndex={isTurnstile ? -1 : 0}
          onClick={activate}
          onKeyDown={onKeyDown}
        >
          <span className="nv-captcha__check" aria-hidden>
            {busy ? <CaptchaSpinner /> : checked ? <CheckIcon width={15} height={15} /> : null}
          </span>
          <span className="nv-captcha__label">{t("auth.login.captcha.label")}</span>
        </div>

        <span className="nv-captcha__badge" aria-hidden>
          <ShieldGlyph />
        </span>
      </div>

      {/* The cloud provider's widget mounts here (renders nothing when no site key is set). */}
      {isTurnstile && <TurnstileWidget onToken={onToken} />}

      <p id={statusId} className="nv-captcha__status" aria-live="polite">
        <span className="nv-captcha__status-text">{statusText}</span>
        {state === "error" && (
          <button type="button" className="nv-captcha__retry" onClick={retry}>
            {t("auth.login.captcha.retry")}
          </button>
        )}
      </p>
    </div>
  );
}

/** A small shield glyph for the box badge — decorative, currentColor, aria-hidden by the parent. */
function ShieldGlyph() {
  return (
    <svg
      width={22}
      height={22}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.75}
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M12 3l7 3v5c0 4.5-3 7.5-7 9-4-1.5-7-4.5-7-9V6l7-3z" />
      <path d="M9.2 12.2l2 2 3.6-3.8" />
    </svg>
  );
}

export default CaptchaField;
